import React, { useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, X } from "lucide-react";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const pad = (n) => String(n).padStart(2, "0");
const monthKey = (y, m) => `${y}-${pad(m + 1)}`; // "2026-04"
const monthLabel = (y, m) => `${MONTH_NAMES[m]} ${y}`;
const monthShort = (y, m) => `${MONTH_NAMES[m].slice(0, 3)} ${y}`;
const lastDayOfMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const startOf = (y, m) => `${y}-${pad(m + 1)}-01`;
const endOf = (y, m) => `${y}-${pad(m + 1)}-${pad(lastDayOfMonth(y, m))}`;

// Get last N month options (key + label) ending at current month
const buildMonthOptions = (count = 24) => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: monthKey(d.getFullYear(), d.getMonth()), y: d.getFullYear(), m: d.getMonth() });
  }
  return out;
};

const monthsBackRange = (n) => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - (n - 1), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: startOf(start.getFullYear(), start.getMonth()),
    end: endOf(end.getFullYear(), end.getMonth()),
  };
};

/**
 * value: { mode: "last" | "months", n?: number, months?: string[] }
 * onChange(value)
 *
 * range(value): { start, end } ISO strings
 */
export function rangeFromValue(value) {
  if (!value || value.mode === "last") {
    return monthsBackRange(value?.n || 1);
  }
  const sorted = [...(value.months || [])].sort();
  if (sorted.length === 0) {
    // default current month
    const now = new Date();
    return {
      start: startOf(now.getFullYear(), now.getMonth()),
      end: endOf(now.getFullYear(), now.getMonth()),
    };
  }
  const [sy, sm] = sorted[0].split("-").map(Number);
  const [ey, em] = sorted[sorted.length - 1].split("-").map(Number);
  return {
    start: startOf(sy, sm - 1),
    end: endOf(ey, em - 1),
  };
}

export default function PeriodSelector({ value, onChange, testid = "period-selector" }) {
  const [open, setOpen] = useState(false);
  const options = useMemo(() => buildMonthOptions(24), []);
  const v = value || { mode: "last", n: 1 };
  const months = v.months || [];

  const toggleMonth = (key) => {
    const set = new Set(months);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    onChange({ mode: "months", months: [...set] });
  };

  const setPreset = (n) => {
    onChange({ mode: "last", n });
  };

  const summary =
    v.mode === "last"
      ? `Last ${v.n} ${v.n === 1 ? "month" : "months"}`
      : months.length === 0
      ? "Pick months"
      : months.length === 1
      ? (() => {
          const [y, m] = months[0].split("-").map(Number);
          return monthShort(y, m - 1);
        })()
      : `${months.length} months selected`;

  return (
    <div className="flex flex-col sm:flex-row gap-2" data-testid={testid}>
      {/* Mode A: Last N */}
      <Select value={v.mode === "last" ? String(v.n) : "__custom"} onValueChange={(val) => {
        if (val === "__custom") {
          onChange({ mode: "months", months: months.length ? months : [options[0].key] });
        } else {
          setPreset(Number(val));
        }
      }}>
        <SelectTrigger className="sm:w-[170px]" data-testid={`${testid}-mode`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {[1, 2, 3, 4, 6, 12, 24].map((n) => (
            <SelectItem key={n} value={String(n)}>
              Last {n} {n === 1 ? "month" : "months"}
            </SelectItem>
          ))}
          <SelectItem value="__custom">Pick specific months…</SelectItem>
        </SelectContent>
      </Select>

      {/* Mode B: Pick specific months (visible only when mode=months) */}
      {v.mode === "months" && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="justify-between sm:min-w-[200px] gap-2"
              data-testid={`${testid}-months-trigger`}
            >
              <span className="truncate">{summary}</span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[280px] p-0"
            data-testid={`${testid}-months-panel`}
          >
            <div className="p-3 border-b border-gray-200 flex items-center justify-between">
              <div className="text-sm font-semibold">
                {months.length} selected
              </div>
              {months.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs gap-1"
                  onClick={() => onChange({ mode: "months", months: [] })}
                >
                  <X className="h-3 w-3" /> Clear
                </Button>
              )}
            </div>
            <div className="max-h-[280px] overflow-y-auto p-1">
              {options.map((o) => {
                const checked = months.includes(o.key);
                return (
                  <label
                    key={o.key}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm hover:bg-gray-50 ${
                      checked ? "bg-gray-100" : ""
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleMonth(o.key)}
                    />
                    <span className="font-mono text-[11px] text-gray-500 w-12">
                      {o.key}
                    </span>
                    <span>{monthLabel(o.y, o.m)}</span>
                  </label>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
